import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import api, { resolveImage } from "../lib/api";
import ProjectCard from "../components/ProjectCard";
import ProjectDetailModal from "../components/ProjectDetailModal";
import { CATEGORIES } from "../components/SubmissionModal";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTheme } from "../context/ThemeContext";

const BANNER_IMG = resolveImage("/api/static/images/banner-makerspace.png");
const BANNER_FALLBACK = "https://images.pexels.com/photos/5553065/pexels-photo-5553065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [department, setDepartment] = useState("");
  const [selected, setSelected] = useState(null);
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const openProject = (p) => {
    if (!user) { toast.info(t.mkt_login_details); navigate("/auth"); return; }
    setSelected(p);
  };
  const { dark } = useTheme();

  useEffect(() => {
    const fetchProjects = () => api.get(`/projects?lang=${lang}`).then((r) => setProjects(r.data)).catch(() => {});
    setLoading(true);
    fetchProjects().finally(() => setLoading(false));
    window.addEventListener("kmu:data-changed", fetchProjects);
    return () => window.removeEventListener("kmu:data-changed", fetchProjects);
  }, [lang]);

  const departments = useMemo(() => [...new Set(projects.map((p) => p.department))].sort(), [projects]);
  const categories = useMemo(() => [...new Set([...CATEGORIES, ...projects.map((p) => p.category)])], [projects]);
  const catLabels = useMemo(() => Object.fromEntries(projects.filter((p) => p.category_label).map((p) => [p.category, p.category_label])), [projects]);
  const deptLabels = useMemo(() => Object.fromEntries(projects.filter((p) => p.department_label).map((p) => [p.department, p.department_label])), [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (category && p.category !== category) return false;
      if (department && p.department !== department) return false;
      if (q && ![p.title, p.tagline, p.description].join(" ").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [projects, search, category, department]);

  return (
    <main data-testid="projects-page" className="pt-0">
      <section className="relative flex h-[46vh] min-h-[340px] items-end overflow-hidden">
        <img
          src={BANNER_IMG}
          onError={(e) => { e.currentTarget.src = BANNER_FALLBACK; }}
          alt="Keimyung members in a maker space"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="projects-banner-image"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${dark ? "from-[#0B1120] via-[#0B1120]/85 to-[#0B1120]/25" : "from-[#F8FAFC] via-[#F8FAFC]/88 to-[#F8FAFC]/20"}`} />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-12 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#1E3A8A]"
          >
            {t.gallery_overline}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl"
          >
            {t.gallery_title_a} <span className="font-serif-accent font-medium italic text-[#2563EB]">{t.gallery_title_b}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-3 max-w-xl text-sm text-slate-600 sm:text-base"
          >
            {t.gallery_subtitle}
          </motion.p>
        </div>
      </section>

      <section className="dots-bg mx-auto max-w-7xl px-5 py-14 lg:px-10">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="projects-search-input"
              placeholder={t.gallery_search}
              className="w-full rounded-full border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm outline-none transition-colors focus:border-[#2563EB]"
            />
          </div>
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            data-testid="projects-department-filter"
            className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm text-slate-600 shadow-sm outline-none transition-colors focus:border-[#2563EB]"
          >
            <option value="">{t.gallery_all_depts}</option>
            {departments.map((d) => <option key={d} value={d}>{deptLabels[d] || d}</option>)}
          </select>
        </div>

        <div className="mt-6 flex flex-wrap gap-2.5" data-testid="projects-category-filters">
          {["", ...categories].map((c) => (
            <button
              key={c || "all"}
              onClick={() => setCategory(c)}
              data-testid={`filter-${c ? c.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all"}`}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                category === c
                  ? "border-[#1E3A8A] bg-[#1E3A8A] text-white shadow-[0_8px_22px_rgba(30,58,138,0.35)]"
                  : "border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
              }`}
            >
              {c ? catLabels[c] || c : t.gallery_all}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-20 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="projects-loading">
            {t.gallery_loading}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-20 rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="projects-empty-state">
            <p className="font-serif-accent text-xl italic text-slate-500">{t.gallery_empty_a}</p>
            <p className="mt-2 text-sm text-slate-400">{t.gallery_empty_b}</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
            {filtered.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} onOpen={openProject} />
            ))}
          </div>
        )}
      </section>

      <ProjectDetailModal project={selected} onClose={() => setSelected(null)} />
    </main>
  );
}
